/* eslint-disable sonarjs/cognitive-complexity */
import type { NextLink, Operation } from '@apollo/client/core';
import { ApolloClient, ApolloLink, Observable } from '@apollo/client/core';
import { InMemoryCache } from '@apollo/client/cache';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { UrlHelper } from '@getaccept/lib-shared/src/helpers/url.helper';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { OperationDefinitionNode } from 'graphql';
import bugsnagClient, { breadcrumbLink } from '@getaccept/lib-shared/src/bugsnag';
import { logOut } from '@getaccept/lib-shared/src/helpers/log-out.helper';
import type { TokenResponse } from '@getaccept/lib-shared/src/types/token-response';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { getPrefixedInternalApiBaseUrl } from '../helpers/get-prefixed-api-base-url';
import { useAuthStore } from './session/store/auth.store';

const intBaseUrl = process.env.VUE_APP_INTERNAL_API_BASE_URL;
const domain = intBaseUrl
  ? getPrefixedInternalApiBaseUrl(intBaseUrl, window.location.hostname)
  : 'localhost:3001';

const httpLink = createHttpLink({
  uri: ({ operationName }) => UrlHelper.getHttpsUrl(`${domain}/graphql?op=${operationName}`),
});

const getAuthToken = () => {
  const authStore = useAuthStore();
  return `Bearer ${authStore.token}`;
};

const handleSessionExpired = ({
  operation,
  forward,
}: {
  operation: Operation;
  forward: NextLink;
}) => {
  const authStore = useAuthStore();
  return new Observable(observer => {
    axios
      .get('/get/settings/token')
      .then((value: AxiosResponse<TokenResponse>) => {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${value.data.access_token}` || null,
          },
        }));

        authStore.setToken(value.data.access_token);

        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch(error => {
        observer.error(error);
        logOut();
      });
  });
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
  if (operation.getContext().errorMiddleware) {
    graphQLErrors = operation.getContext().errorMiddleware(graphQLErrors);
  }
  if (graphQLErrors) {
    const sessionExpired = graphQLErrors.some(
      ({ extensions }) => extensions?.code === 'UNAUTHENTICATED'
    );
    if (sessionExpired && !operation.getContext().disableTokenRetry) {
      return handleSessionExpired({ operation, forward });
    }
    const inactiveUser = graphQLErrors.some(
      error => error.message === 'User profile is not active'
    );

    if (inactiveUser && !window.location.href.includes(PathName.InactiveUser)) {
      window.location.href = `/${PathName.InactiveUser}`;
    }

    if (bugsnagClient) {
      graphQLErrors
        .filter(
          error =>
            !error.extensions ||
            error.extensions.code !== 'UNAUTHENTICATED' ||
            error.message !== 'User profile is not active'
        )
        .forEach(graphQLError => {
          bugsnagClient.notify(new Error(JSON.stringify(graphQLError)), event => {
            event.groupingHash =
              typeof graphQLError.message === 'string' &&
              graphQLError.message.startsWith('Documents not found: ')
                ? 'Documents not found: '
                : graphQLError.message.startsWith('Forbidden file: ')
                ? 'Forbidden file: '
                : graphQLError.message.startsWith('Access denied for documents: ')
                ? 'Access denied for documents: '
                : graphQLError.message;
            event.addMetadata('GraphQL', {
              operationName: operation?.operationName,
              variables: operation?.variables,
              type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
            });
          });
        });
    }
  }

  if (networkError && Object.keys(networkError).length && bugsnagClient && !graphQLErrors?.length) {
    if (networkError?.message === 'Your session expired. Sign in again.') {
      return;
    }
    bugsnagClient.notify(new Error(JSON.stringify(networkError)), event => {
      event.groupingHash = JSON.stringify(networkError);
      event.addMetadata('GraphQL', {
        operationName: operation?.operationName,
        variables: operation?.variables,
        type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
      });
    });
  }
});

const authLink = new ApolloLink((operation, forward) => {
  if (operation.getContext().headers?.authorization) {
    operation.setContext({
      headers: { authorization: operation.getContext().headers?.authorization },
      disableTokenRetry: true,
    });
    return forward(operation);
  }
  operation.setContext({ headers: { authorization: getAuthToken() } });
  return forward(operation);
});

export const apolloClientInternal = new ApolloClient({
  link: ApolloLink.from([authLink, breadcrumbLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  queryDeduplication: false,
});
